import type { SalesOpportunity } from "@color-finder/color-finder-api/models";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";

export interface HistorySettings {
  opportunityAutocomplete: Array<HistoryEntry<SalesOpportunity>>;
}

export interface HistoryEntry<T extends { id: string }> {
  time: Date | string;
  value: T;
}

const HISTORY_LENGTH = 5;
const MAX_AGE_DAYS = 7;

export const useHistorySettings = <
  T extends {
    id: string;
  },
>(
  action: keyof HistorySettings,
): [
  history: Array<HistoryEntry<T>>,
  addToHistory: (v: HistoryEntry<T>) => void,
] => {
  const initialValue = useMemo(
    () => ({
      opportunityAutocomplete: [],
    }),
    [],
  );
  const [settings, setSettings] = useLocalStorage<HistorySettings>(
    "color-finder-history-settings",
    initialValue,
  );

  const addToHistory = (entry: HistoryEntry<T>) => {
    const newSettings: Array<HistoryEntry<T>> = [
      ...(settings[action] as any),
      entry,
    ]
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .filter(
        (a) =>
          new Date(a.time).getTime() >
          Date.now() - 1000 * 60 * 60 * 24 * MAX_AGE_DAYS,
      )
      .filter((v, i, a) => a.findIndex((e) => e.value.id === v.value.id) === i)
      .slice(0, HISTORY_LENGTH);
    setSettings({
      ...settings,
      [action]: newSettings,
    });
  };

  return [settings[action] as any, addToHistory];
};
